import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Button,
  Grid,
} from "@mui/material";
import httpRequest from "../utils/httpRequest";

const WhatsAppNotifications = () => {
  const [notifications, setNotifications] = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    date: '',
  });

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await httpRequest(
        "whatsapp/notifications",
        "GET",
        {},
        { page, pageSize, ...filters }
      );
      setNotifications(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching notifications:", err);
      setError("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [page, pageSize, filters]);

  const handlePageChange = (_, value) => {
    setPage(value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(parseInt(e.target.value));
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      date: '',
    });
    setPage(1);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: 1, width: "100%" }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        WhatsApp Notifications History
      </Typography>

      {/* Date Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Filter by Date"
              type="date"
              value={filters.date}
              onChange={(e) => handleFilterChange('date', e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              size="small"
              sx={{ height: 40 }}
            >
              Clear Filter
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="notifications table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Mobile</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {notifications.data.length ? (
              notifications.data.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell sx={{ fontSize: '0.875rem' }}>
                    {formatDate(notification.created_at)}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>
                    {notification.Invoice ? (
                      <>
                        <div>{notification.Invoice.invoice_number}</div>
                        <div style={{ color: '#666', fontSize: '0.75rem' }}>
                          {notification.Invoice.type} - ₹{notification.Invoice.grand_total}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ color: '#1976d2', fontWeight: 'bold' }}>Statement</div>
                        <div style={{ color: '#666', fontSize: '0.75rem' }}>
                          Account Statement
                        </div>
                      </>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>
                    <div>{notification.Customer?.name || 'N/A'}</div>
                    <div style={{ color: '#666', fontSize: '0.75rem' }}>
                      {notification.Customer?.account_number}
                    </div>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.875rem' }}>{notification.recipient_mobile}</TableCell>
                  <TableCell>
                    <Chip
                      label={notification.status}
                      color={getStatusColor(notification.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: 1, 
                      p: 1.5, 
                      border: '1px solid #e0e0e0',
                      maxWidth: 400
                    }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          wordBreak: 'break-word',
                          lineHeight: 1.4,
                          fontSize: '0.875rem',
                          color: '#333'
                        }}
                      >
                        {notification.message}
                      </Typography>
                      {notification.file_name && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#666',
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            📎 Attachment: {notification.file_name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {notification.error_message && (
                      <Box sx={{ 
                        backgroundColor: '#ffebee', 
                        borderRadius: 1, 
                        p: 1.5, 
                        border: '1px solid #ffcdd2',
                        maxWidth: 300
                      }}>
                        <Typography 
                          variant="body2" 
                          color="error" 
                          sx={{ 
                            wordBreak: 'break-word',
                            lineHeight: 1.4,
                            fontSize: '0.875rem'
                          }}
                        >
                          {notification.error_message}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No notifications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
      >
        <FormControl size="small">
          <InputLabel id="page-size-label">Rows</InputLabel>
          <Select
            labelId="page-size-label"
            value={pageSize}
            onChange={handlePageSizeChange}
            label="Rows"
          >
            {[5, 10, 20, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Pagination
          count={notifications.pagination?.totalPages || 1}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default WhatsAppNotifications; 